import * as React from 'react';
import { useRef } from 'react';
import type { CSSProperties } from 'react';
import type { CellEntry } from '@ooh/contracts';
import type {
  AvailabilityGridData,
  AvailabilityGridRow,
  AvailabilityPeriod,
  CellState,
  CellSelection,
} from '../lib/availability-types';
import { CellStateIcon, getCellTdClasses, getHatchStyle } from './availability-cell-styles';
import { MultiStackCellContent } from './multi-stack-cell-content';
import type { CellPopoverLabels } from './cell-popover-state';
import { CellPopover } from './availability-cell-popover';
import { isMultiSelectEligibleState } from './availability-grid-selection';

export function isCellClickable(
  cellState: CellState,
  onCellClick:
    | ((unitId: string, period: AvailabilityPeriod, shiftKey: boolean) => void)
    | undefined,
  soldCellClickable: boolean,
): boolean {
  if (onCellClick === undefined) return false;
  // SOLD cells: clickable only when the caller opts in (e.g. inventory view for read-only popover).
  // In the offer-availability view, SOLD cells are never interactable.
  if (cellState === 'sold') return soldCellClickable;
  return true;
}

function resolveCellContent(
  customContent: React.ReactNode,
  entries: CellEntry[] | undefined,
  cellState: CellState,
  colWidth: number | undefined,
  soldAriaLabel: string,
  optionAriaLabel?: string,
  reservedAriaLabel?: string,
): React.ReactNode {
  if (customContent !== undefined) return customContent;
  if (entries && entries.length > 0) {
    return <MultiStackCellContent entries={entries} colWidth={colWidth} />;
  }
  return (
    <CellStateIcon
      state={cellState}
      soldAriaLabel={soldAriaLabel}
      optionAriaLabel={optionAriaLabel}
      reservedAriaLabel={reservedAriaLabel}
    />
  );
}

// eslint-disable-next-line complexity -- optional queue affordance added one branch; extraction would split a cohesive cell renderer.
export function CellTd({
  row,
  period,
  cellState,
  isSelected,
  isClickable,
  showPopover,
  disableInternalPopover = false,
  selection,
  periods,
  grid,
  entries,
  colWidth,
  popoverLabels,
  soldAriaLabel,
  optionAriaLabel,
  reservedAriaLabel,
  getReservationStatusForPeriod,
  getOwnCellTtlExpiresAt,
  onTtlExpired,
  selectedCellClassName,
  selectedCellStyle,
  onCellClick,
  onClosePopover,
  onReserve,
  onUpgradeHard,
  onCloseSale,
  onRelease,
  queueInfo,
  onJoinQueue,
  onLeaveQueue,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  customContent,
}: {
  row: AvailabilityGridRow;
  period: AvailabilityPeriod;
  cellState: CellState;
  isSelected: boolean;
  isClickable: boolean;
  showPopover: boolean;
  /** When true, never renders the internal CellPopover even when showPopover is true. */
  disableInternalPopover?: boolean;
  selection?: CellSelection | null;
  periods: AvailabilityPeriod[];
  grid: AvailabilityGridData;
  /** Multi-stack entries for this cell; when provided, renders MultiStackCellContent. */
  entries?: CellEntry[];
  colWidth?: number;
  popoverLabels: CellPopoverLabels;
  soldAriaLabel: string;
  /** Accessible label for the OPTION-bucket status icon fallback (no customContent/entries). */
  optionAriaLabel?: string;
  /** Accessible label for the merged reserved-bucket status icon fallback (no customContent/entries). */
  reservedAriaLabel?: string;
  getReservationStatusForPeriod?: (
    unitId: string,
    periodId: string,
  ) => 'SOFT_RESERVATION' | 'HARD_RESERVATION' | null;
  getOwnCellTtlExpiresAt?: (unitId: string, periodId: string) => Date | null | undefined;
  selectedCellClassName?: string;
  selectedCellStyle?: CSSProperties;
  onCellClick?: (unitId: string, period: AvailabilityPeriod, shiftKey: boolean) => void;
  onTtlExpired?: () => void;
  onClosePopover?: () => void;
  onReserve?: (selection: CellSelection, status: 'SOFT_RESERVATION' | 'OPTION') => void;
  onUpgradeHard?: (selection: CellSelection) => void;
  onCloseSale?: (selection: CellSelection) => void;
  onRelease?: (selection: CellSelection) => void;
  queueInfo?: { count: number; myPosition: number | null } | null;
  onJoinQueue?: (selection: CellSelection) => void;
  onLeaveQueue?: (selection: CellSelection) => void;
  /** Called on mousedown on a FREE cell to begin a drag selection. */
  onCellMouseDown?: (unitId: string, period: AvailabilityPeriod) => void;
  /** Called on mouseenter while drag is active. */
  onCellMouseEnter?: (unitId: string, period: AvailabilityPeriod) => void;
  /** Called on mouseup to commit the drag selection. */
  onCellMouseUp?: (unitId: string, period: AvailabilityPeriod) => void;
  customContent?: React.ReactNode;
}) {
  const tdRef = useRef<HTMLTableCellElement>(null);

  // A window-sourced cell's entries carry fromAvailabilityWindow=true. Checking
  // across all entries (not just index 0) avoids depending on an unenforced
  // backend ordering invariant.
  const isWindowSourced = entries?.some((e) => e.fromAvailabilityWindow === true) ?? false;

  return (
    <td
      ref={tdRef}
      data-testid={`cell-${row.id}-${period.id}`}
      data-cell-state={cellState}
      data-cell-row={row.id}
      data-cell-period={period.id}
      data-unified-select-cell
      className={`h-10 ${getCellTdClasses(
        cellState,
        isSelected,
        selectedCellClassName,
        isClickable,
        isWindowSourced,
      )}`}
      style={{
        ...getHatchStyle(cellState),
        ...(isSelected ? selectedCellStyle : undefined),
        position: 'relative',
      }}
      onClick={(event) => {
        if (!isClickable || (event.shiftKey && !isMultiSelectEligibleState(cellState))) {
          return;
        }
        onCellClick?.(row.id, period, event.shiftKey);
      }}
      onMouseDown={() => onCellMouseDown?.(row.id, period)}
      onMouseEnter={() => onCellMouseEnter?.(row.id, period)}
      onMouseUp={() => onCellMouseUp?.(row.id, period)}
    >
      <div className="h-full w-full flex items-center justify-center">
        {resolveCellContent(
          customContent,
          entries,
          cellState,
          colWidth,
          soldAriaLabel,
          optionAriaLabel,
          reservedAriaLabel,
        )}
      </div>
      {!disableInternalPopover && showPopover && selection != null && onClosePopover ? (
        <CellPopover
          selection={selection}
          periods={periods}
          unitLabel={row.label}
          labels={popoverLabels}
          getCellStateForPeriod={(periodId) => grid[row.id]?.[periodId] ?? 'free'}
          getReservationStatusForPeriod={(periodId) =>
            getReservationStatusForPeriod?.(row.id, periodId) ?? null
          }
          entries={entries}
          ttlExpiresAt={getOwnCellTtlExpiresAt?.(row.id, selection?.periodIds[0] ?? '')}
          onExpired={onTtlExpired}
          anchorRef={tdRef}
          onClose={onClosePopover}
          onReserve={onReserve}
          onUpgradeHard={onUpgradeHard}
          onCloseSale={onCloseSale}
          onRelease={onRelease}
          queueInfo={queueInfo ?? undefined}
          onJoinQueue={onJoinQueue}
          onLeaveQueue={onLeaveQueue}
        />
      ) : null}
    </td>
  );
}
